import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ExamRevisionBotexamidLangGridQueryKey,
  usePostApiV1ExamRevisionLangBotexamlangidFinishTranslation,
} from '@/shared/api/generated';

/**
 * Finaliza a tradução de um idioma via
 * `POST /api/v1/exam/revision/lang/{botExamLangId}/finish-translation`
 * (marca todas as questões como revisadas e recalcula o status da prova),
 * invalidando a grid de idiomas ao concluir.
 *
 * @param botExamId - ID da prova bot à qual o idioma pertence (para invalidação)
 * @returns Handler de finalização e estado de loading
 *
 * @example
 * ```tsx
 * const { finishTranslation, isLoading } = useFinishRevisionTranslation(botExamId);
 * finishTranslation(botExamLangId, { onSuccess: () => navigate(...) });
 * ```
 */
export function useFinishRevisionTranslation(botExamId: number) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('examRevision');

  const mutation = usePostApiV1ExamRevisionLangBotexamlangidFinishTranslation({
    mutation: {
      onSuccess: () => {
        alert({ alertType: 'success', title: t('translation.toast.finishSuccess') });
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamRevisionBotexamidLangGridQueryKey(botExamId),
        });
      },
      onError: () => alert({ alertType: 'warning', title: t('translation.toast.finishError') }),
    },
  });

  const finishTranslation = (botExamLangId: number, callbacks?: { onSuccess?: () => void }) => {
    mutation.mutate(
      { botExamLangId },
      callbacks?.onSuccess ? { onSuccess: callbacks.onSuccess } : undefined,
    );
  };

  return { finishTranslation, isLoading: mutation.isPending } as const;
}
